import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Key,
  Shield,
  Plus,
  Trash2,
  Edit,
  Copy,
  RefreshCw,
  Check,
  X,
  Loader2,
  Globe,
  AlertCircle,
  Fingerprint,
  Smartphone,
  Usb,
  Download,
  CheckCircle2,
  Clock,
  Info,
  ArrowLeft,
  AlertTriangle,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { advancedSecurityApi } from "@/services/api";
import { generateUUID } from "@/utils/transform";

// ============================================
// CONFIRM DIALOG COMPONENT
// ============================================
const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  loading = false,
  onConfirm,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {variant === "destructive" && (
            <AlertTriangle className="h-5 w-5 text-destructive" />
          )}
          {title}
        </DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={loading}
        >
          {cancelText}
        </Button>
        <Button
          variant={variant === "destructive" ? "destructive" : "default"}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {confirmText}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AdvancedSecuritySettings = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("webauthn");

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page header with back link */}
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="flex items-center justify-center h-10 w-10 sm:h-8 sm:w-8 rounded-lg border hover:bg-accent transition-colors touch-target"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("settings.advancedSecurity.title", "Advanced Security")}
          </h1>
          <p className="text-muted-foreground">
            {t(
              "settings.advancedSecurity.description",
              "IP allowlist, WebAuthn keys, and backup codes",
            )}
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="webauthn" className="gap-2">
            <Key className="h-4 w-4" />
            Security Keys
          </TabsTrigger>
          <TabsTrigger value="backup" className="gap-2">
            <Shield className="h-4 w-4" />
            Backup Codes
          </TabsTrigger>
          <TabsTrigger value="ip" className="gap-2">
            <Globe className="h-4 w-4" />
            IP Allowlist
          </TabsTrigger>
        </TabsList>

        <TabsContent value="webauthn">
          <WebAuthnSection />
        </TabsContent>

        <TabsContent value="backup">
          <BackupCodesSection />
        </TabsContent>

        <TabsContent value="ip">
          <IpAllowlistSection />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============================================
// WEBAUTHN SECTION
// ============================================
const WebAuthnSection = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [registering, setRegistering] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [pendingCredential, setPendingCredential] = useState(null);
  const [credentialName, setCredentialName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    id: null,
    name: "",
  });

  // ============================================
  // TanStack Query - Fetch Credentials
  // ============================================
  const { data: credentials = [], isLoading: loading } = useQuery({
    queryKey: ["webauthn-credentials"],
    queryFn: async () => {
      const response = await advancedSecurityApi.getWebAuthnCredentials();
      return response.data?.data || [];
    },
  });

  // ============================================
  // TanStack Query - Mutations
  // ============================================
  const verifyRegistrationMutation = useMutation({
    mutationFn: ({ credential, name }) =>
      advancedSecurityApi.verifyWebAuthnRegistration({
        credential,
        name,
      }),
    onSuccess: () => {
      toast.success("Security key registered successfully");
      setShowNameDialog(false);
      setPendingCredential(null);
      queryClient.invalidateQueries({ queryKey: ["webauthn-credentials"] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to verify registration",
      );
    },
  });

  const deleteCredentialMutation = useMutation({
    mutationFn: (id) => advancedSecurityApi.deleteWebAuthnCredential(id),
    onSuccess: () => {
      toast.success("Security key removed");
      setDeleteConfirm({ open: false, id: null, name: "" });
      queryClient.invalidateQueries({ queryKey: ["webauthn-credentials"] });
    },
    onError: () => {
      toast.error("Failed to remove security key");
    },
  });

  const renameCredentialMutation = useMutation({
    mutationFn: ({ id, name }) =>
      advancedSecurityApi.renameWebAuthnCredential(id, { name }),
    onSuccess: () => {
      toast.success("Security key renamed");
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ["webauthn-credentials"] });
    },
    onError: () => {
      toast.error("Failed to rename security key");
    },
  });

  const handleRegister = async () => {
    setRegistering(true);
    try {
      // Get registration options from server
      const optionsResponse =
        await advancedSecurityApi.getWebAuthnRegistrationOptions();
      const options = optionsResponse.data?.data;

      if (!options) throw new Error("Failed to get registration options");

      // Convert challenge from base64url
      options.challenge = base64urlToBuffer(options.challenge);
      options.user.id = base64urlToBuffer(options.user.id);

      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map((cred) => ({
          ...cred,
          id: base64urlToBuffer(cred.id),
        }));
      }

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: options,
      });

      if (!credential) throw new Error("Failed to create credential");

      // Prepare credential for server
      const credentialForServer = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          attestationObject: bufferToBase64url(
            credential.response.attestationObject,
          ),
          transports: credential.response.getTransports?.() || [],
        },
        authenticatorAttachment: credential.authenticatorAttachment,
      };

      setPendingCredential(credentialForServer);
      setCredentialName("");
      setShowNameDialog(true);
    } catch (error) {
      console.error("WebAuthn registration error:", error);
      if (error.name === "NotAllowedError") {
        toast.error(
          t("settings.advancedSecurity.registrationCancelledOrTimedOut"),
        );
      } else if (error.name === "NotSupportedError") {
        toast.error(t("settings.advancedSecurity.browserNotSupported"));
      } else {
        toast.error(
          error.message || t("settings.advancedSecurity.failedToRegister"),
        );
      }
    } finally {
      setRegistering(false);
    }
  };

  const completeRegistration = () => {
    if (!pendingCredential) return;
    verifyRegistrationMutation.mutate({
      credential: pendingCredential,
      name: credentialName || "Security Key",
    });
  };

  const handleDeleteClick = (cred) => {
    setDeleteConfirm({ open: true, id: cred.id, name: cred.name });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.id) {
      deleteCredentialMutation.mutate(deleteConfirm.id);
    }
  };

  const handleRename = (id) => {
    renameCredentialMutation.mutate({ id, name: editName });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Security Keys (WebAuthn)
              </CardTitle>
              <CardDescription>
                Use hardware security keys or built-in authenticators for
                passwordless sign-in
              </CardDescription>
            </div>
            <Button onClick={handleRegister} disabled={registering}>
              {registering ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add Security Key
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {credentials.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Fingerprint className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No security keys registered yet</p>
              <p className="text-sm mt-1">
                Add a security key for passwordless authentication
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead className="w-25">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {credentials.map((cred) => (
                  <TableRow key={cred.id}>
                    <TableCell>
                      {editingId === cred.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="h-8 w-40"
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleRename(cred.id)}
                            aria-label="Save name"
                          >
                            <Check className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setEditingId(null)}
                            aria-label="Cancel editing"
                          >
                            <X className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {cred.deviceType === "platform" ? (
                            <Smartphone
                              className="h-4 w-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                          ) : (
                            <Usb
                              className="h-4 w-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                          )}
                          <span>{cred.name}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {cred.deviceType === "platform"
                          ? "Built-in"
                          : "Security Key"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(cred.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {cred.lastUsedAt
                        ? new Date(cred.lastUsedAt).toLocaleDateString()
                        : "Never"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            setEditingId(cred.id);
                            setEditName(cred.name);
                          }}
                          aria-label="Edit name"
                        >
                          <Edit className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDeleteClick(cred)}
                          aria-label="Delete security key"
                        >
                          <Trash2
                            className="h-4 w-4 text-destructive"
                            aria-hidden="true"
                          />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Name Dialog */}
      <Dialog open={showNameDialog} onOpenChange={setShowNameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Name Your Security Key</DialogTitle>
            <DialogDescription>
              Give this security key a name to help you identify it later
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="keyName">Name</Label>
            <Input
              id="keyName"
              value={credentialName}
              onChange={(e) => setCredentialName(e.target.value)}
              placeholder="e.g., YubiKey 5, MacBook Touch ID"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNameDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={completeRegistration}
              disabled={verifyRegistrationMutation.isPending}
            >
              {verifyRegistrationMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm((prev) => ({ ...prev, open }))}
        title="Remove Security Key"
        description={`Are you sure you want to remove "${deleteConfirm.name}"? You will no longer be able to use this key to sign in.`}
        confirmText="Remove"
        variant="destructive"
        loading={deleteCredentialMutation.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

// ============================================
// BACKUP CODES SECTION
// ============================================
const BackupCodesSection = () => {
  const queryClient = useQueryClient();
  const [showCodesDialog, setShowCodesDialog] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [newCodes, setNewCodes] = useState([]);
  const [copied, setCopied] = useState(false);

  // ============================================
  // TanStack Query - Fetch Status
  // ============================================
  const { data: status, isLoading: loading } = useQuery({
    queryKey: ["backup-codes-status"],
    queryFn: async () => {
      const response = await advancedSecurityApi.getBackupCodesStatus();
      return response.data?.data;
    },
  });

  // ============================================
  // TanStack Query - Generate Codes Mutation
  // ============================================
  const generateCodesMutation = useMutation({
    mutationFn: () => advancedSecurityApi.generateBackupCodes(),
    onSuccess: (response) => {
      setNewCodes(response.data?.data?.codes || []);
      setShowCodesDialog(true);
      setShowRegenerateConfirm(false);
      queryClient.invalidateQueries({ queryKey: ["backup-codes-status"] });
    },
    onError: () => {
      toast.error("Failed to generate backup codes");
    },
  });

  const generating = generateCodesMutation.isPending;

  const handleGenerate = () => {
    if (status?.hasBackupCodes) {
      setShowRegenerateConfirm(true);
    } else {
      generateCodesMutation.mutate();
    }
  };

  const handleRegenerateConfirm = () => {
    generateCodesMutation.mutate();
  };

  const handleCopy = () => {
    const text = newCodes.join("\n");
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Codes copied to clipboard");
  };

  const handleDownload = () => {
    const text = `eScribe Backup Codes\n${"=".repeat(
      30,
    )}\n\nGenerated: ${new Date().toLocaleString()}\n\nKeep these codes in a safe place. Each code can only be used once.\n\n${newCodes.join(
      "\n",
    )}`;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "eecribe-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderProperButtonIcon = () => {
    if (generating) {
      return <Loader2 className="h-4 w-4 mr-2 animate-spin" />;
    }
    return status?.hasBackupCodes ? (
      <RefreshCw className="h-4 w-4 mr-2" />
    ) : (
      <Plus className="h-4 w-4 mr-2" />
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Backup Codes
              </CardTitle>
              <CardDescription>
                One-time use codes for account recovery when you can't access
                your primary 2FA method
              </CardDescription>
            </div>
            <Button onClick={handleGenerate} disabled={generating}>
              {renderProperButtonIcon()}
              {status?.hasBackupCodes ? "Regenerate" : "Generate"} Codes
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {status?.hasBackupCodes ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
                <div>
                  <p className="font-medium">Backup codes are enabled</p>
                  <p className="text-sm text-muted-foreground">
                    {status.remainingCodes} of {status.totalCodes} codes
                    remaining
                  </p>
                </div>
              </div>
              {status.remainingCodes <= 2 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    You're running low on backup codes. Consider generating new
                    ones.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No backup codes generated yet</p>
              <p className="text-sm mt-1">
                Generate backup codes for emergency account access
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Regenerate Confirmation Dialog */}
      <ConfirmDialog
        open={showRegenerateConfirm}
        onOpenChange={setShowRegenerateConfirm}
        title="Regenerate Backup Codes"
        description="This will invalidate all existing backup codes. Any unused codes will no longer work. Are you sure you want to continue?"
        confirmText="Regenerate Codes"
        variant="destructive"
        loading={generating}
        onConfirm={handleRegenerateConfirm}
      />

      {/* New Codes Dialog */}
      <Dialog open={showCodesDialog} onOpenChange={setShowCodesDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Your New Backup Codes</DialogTitle>
            <DialogDescription>
              Save these codes in a secure location. Each code can only be used
              once.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-muted p-4 rounded-lg font-mono text-sm grid grid-cols-2 gap-2">
              {newCodes.map((code) => (
                <div key={generateUUID()} className="text-center">
                  {code}
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={handleCopy}>
              {copied ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}
              {copied ? "Copied!" : "Copy"}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleDownload}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowCodesDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// IP ALLOWLIST SECTION
// ============================================
const IpAllowlistSection = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [accessLog, setAccessLog] = useState([]);
  const [newEntry, setNewEntry] = useState({
    ipAddress: "",
    cidrRange: "",
    description: "",
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    id: null,
    ip: "",
  });

  // ============================================
  // TanStack Query - Fetch Settings
  // ============================================
  const { data, isLoading: loading } = useQuery({
    queryKey: ["ip-allowlist-settings"],
    queryFn: async () => {
      const response = await advancedSecurityApi.getIpAllowlistSettings();
      return response.data?.data;
    },
  });

  const enabled = data?.enabled ?? false;
  const entries = data?.entries ?? [];
  const currentIp = data?.currentIp ?? "";

  // ============================================
  // TanStack Query - Mutations
  // ============================================
  const toggleEnabledMutation = useMutation({
    mutationFn: (value) =>
      advancedSecurityApi.updateIpAllowlistSettings({ enabled: value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ip-allowlist-settings"] });
    },
    onError: () => {
      toast.error("Failed to update settings");
    },
  });

  const addEntryMutation = useMutation({
    mutationFn: (entry) => advancedSecurityApi.addIpEntry(entry),
    onSuccess: () => {
      setShowAddDialog(false);
      setNewEntry({ ipAddress: "", cidrRange: "", description: "" });
      toast.success("IP added to allowlist");
      queryClient.invalidateQueries({ queryKey: ["ip-allowlist-settings"] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to add IP");
    },
  });

  const deleteEntryMutation = useMutation({
    mutationFn: (id) => advancedSecurityApi.deleteIpEntry(id),
    onSuccess: () => {
      toast.success("IP removed from allowlist");
      setDeleteConfirm({ open: false, id: null, ip: "" });
      queryClient.invalidateQueries({ queryKey: ["ip-allowlist-settings"] });
    },
    onError: () => {
      toast.error("Failed to remove IP");
    },
  });

  const toggleEntryMutation = useMutation({
    mutationFn: ({ id, isEnabled }) =>
      advancedSecurityApi.updateIpEntry(id, { isEnabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ip-allowlist-settings"] });
    },
    onError: () => {
      toast.error("Failed to update entry");
    },
  });

  const saving = addEntryMutation.isPending;

  const handleToggle = (value) => {
    toggleEnabledMutation.mutate(value);
  };

  const handleAdd = () => {
    if (!newEntry.ipAddress) {
      toast.error("Please enter an IP address");
      return;
    }
    addEntryMutation.mutate(newEntry);
  };

  const handleDeleteClick = (entry) => {
    setDeleteConfirm({ open: true, id: entry.id, ip: entry.ipAddress });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.id) {
      deleteEntryMutation.mutate(deleteConfirm.id);
    }
  };

  const handleToggleEntry = (entry) => {
    toggleEntryMutation.mutate({ id: entry.id, isEnabled: !entry.isEnabled });
  };

  const loadAccessLog = async () => {
    try {
      const response = await advancedSecurityApi.getIpAccessLog();
      setAccessLog(response.data?.data || []);
      setShowLog(true);
    } catch {
      toast.error("Failed to load access log");
    }
  };

  const addCurrentIp = () => {
    setNewEntry({ ...newEntry, ipAddress: currentIp });
    setShowAddDialog(true);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                IP Allowlist
              </CardTitle>
              <CardDescription>
                Restrict access to your organisation from specific IP addresses
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={enabled} onCheckedChange={handleToggle} />
                <span className="text-sm">
                  {enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current IP Info */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Your current IP:</span>
              <code className="text-sm font-mono bg-background px-2 py-1 rounded">
                {currentIp}
              </code>
            </div>
            <Button variant="outline" size="sm" onClick={addCurrentIp}>
              Add Current IP
            </Button>
          </div>

          {enabled && entries.length === 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Warning: IP allowlist is enabled but no IPs are configured. All
                access will be blocked except from currently active sessions.
              </AlertDescription>
            </Alert>
          )}

          {/* IP List */}
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Allowed IPs ({entries.length})</h4>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadAccessLog}>
                <Clock className="h-4 w-4 mr-2" />
                View Log
              </Button>
              <Button size="sm" onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add IP
              </Button>
            </div>
          </div>

          {entries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No IP addresses configured</p>
              <p className="text-sm mt-1">
                Add IP addresses to restrict access
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>IP Address</TableHead>
                  <TableHead>CIDR</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="w-25">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-mono">
                      {entry.ipAddress}
                    </TableCell>
                    <TableCell className="font-mono text-muted-foreground">
                      {entry.cidrRange || "-"}
                    </TableCell>
                    <TableCell>{entry.description || "-"}</TableCell>
                    <TableCell>
                      <Switch
                        checked={entry.isEnabled}
                        onCheckedChange={() => handleToggleEntry(entry)}
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(entry.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDeleteClick(entry)}
                        aria-label="Delete IP"
                      >
                        <Trash2
                          className="h-4 w-4 text-destructive"
                          aria-hidden="true"
                        />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm((prev) => ({ ...prev, open }))}
        title="Remove IP Address"
        description={`Are you sure you want to remove ${deleteConfirm.ip} from the allowlist?`}
        confirmText="Remove"
        variant="destructive"
        loading={deleteEntryMutation.isPending}
        onConfirm={handleDeleteConfirm}
      />

      {/* Add IP Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add IP Address</DialogTitle>
            <DialogDescription>
              Add an IP address or CIDR range to the allowlist
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ipAddress">IP Address *</Label>
              <Input
                id="ipAddress"
                value={newEntry.ipAddress}
                onChange={(e) =>
                  setNewEntry({ ...newEntry, ipAddress: e.target.value })
                }
                placeholder="192.168.1.1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cidrRange">CIDR Range (optional)</Label>
              <Input
                id="cidrRange"
                value={newEntry.cidrRange}
                onChange={(e) =>
                  setNewEntry({ ...newEntry, cidrRange: e.target.value })
                }
                placeholder="192.168.1.0/24"
              />
              <p className="text-xs text-muted-foreground">
                Use CIDR notation to allow a range of IPs
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Input
                id="description"
                value={newEntry.description}
                onChange={(e) =>
                  setNewEntry({ ...newEntry, description: e.target.value })
                }
                placeholder="Office network, VPN, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add IP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Log Dialog */}
      <Dialog open={showLog} onOpenChange={setShowLog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>IP Access Log</DialogTitle>
            <DialogDescription>
              Recent IP access attempts to your organisation
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accessLog.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-muted-foreground"
                    >
                      No access log entries
                    </TableCell>
                  </TableRow>
                ) : (
                  accessLog.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-muted-foreground">
                        {new Date(log.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell className="font-mono">
                        {log.ipAddress}
                      </TableCell>
                      <TableCell>{log.userEmail || "-"}</TableCell>
                      <TableCell>
                        {log.action === "allowed" ? (
                          <Badge
                            variant="success"
                            className="bg-green-100 text-green-700"
                          >
                            Allowed
                          </Badge>
                        ) : (
                          <Badge variant="destructive">Blocked</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowLog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const base64urlToBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

export default AdvancedSecuritySettings;
